import useRequest from '@swarm/core/hooks/async/useRequest'
import { MainnetNetwork } from '@swarm/core/services/gas-estimator'
import rewardsApi from '@swarm/core/services/rewardsApi'
import { NetworkMap } from '@swarm/core/shared/consts'
import securitiesRewards from '@swarm/core/shared/consts/securities-rewards'
import { SupportedNetworkId } from '@swarm/core/shared/enums/supported-network-id'
import { isMainnet as checkMainnet } from '@swarm/core/shared/utils/config'
import { parseNumber } from '@swarm/core/shared/utils/lodash'
import { unifyAddress, useNetwork } from '@swarm/core/web3'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

const { OWNER, REPOSITORY, TREE_SHA } = securitiesRewards

const useSecuritiesRewards = () => {
  const { networkName, networkId } = useNetwork()
  const refetched = useRef(false)
  const [apyReportNumber, setApyReportNumber] = useState<number>()

  const { loading: treeLoading } = useRequest(
    useCallback(async () => {
      const treeInfo = await rewardsApi.getTreeInfo(OWNER, REPOSITORY, TREE_SHA)

      const reportsTree = treeInfo.tree.find((tree) => tree.path === 'reports')

      if (!reportsTree) return
      const reportsInfo = await rewardsApi.getTreeInfo(
        OWNER,
        REPOSITORY,
        reportsTree?.sha,
      )
      const lastTreeIdx = reportsInfo.tree.length - 1
      const APYReportNumber = parseNumber(reportsInfo.tree[lastTreeIdx].path)
      setApyReportNumber(APYReportNumber)
    }, []),
  )

  const { data, loading, error, ready, refetch } = useRequest(
    useCallback(async () => {
      const isMainnet = checkMainnet(networkId)
      const network = isMainnet
        ? networkName
        : NetworkMap.get(SupportedNetworkId.Polygon)?.networkName

      if (!apyReportNumber || !network) return null

      const response = await rewardsApi.getSecuritiesSmtRewards(
        apyReportNumber,
        network?.toLowerCase() as MainnetNetwork,
      )

      return response
    }, [apyReportNumber, networkId, networkName]),
  )

  useEffect(() => {
    if (
      !data &&
      apyReportNumber &&
      !loading &&
      !refetched.current &&
      !treeLoading
    ) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      setApyReportNumber((currNum) => currNum! - 1)
      refetch()
      refetched.current = true
    }
  }, [apyReportNumber, data, error, loading, ready, refetch, treeLoading])

  return useMemo(() => {
    return {
      securitiesRewards: data
        ? Object.fromEntries(
            Object.entries(data.securitiesrewards).map(([key, value]) => [
              unifyAddress(key),
              value,
            ]),
          )
        : {},
      loading: loading || !apyReportNumber || !data,
    }
  }, [apyReportNumber, data, loading])
}

export default useSecuritiesRewards
